import { useAuth } from '../contexts/AuthContext';
import { LoginBrand } from '../components/auth/LoginBrand';
import { VerifyingOverlay } from '../components/ui/VerifyingOverlay';
import { canAccessAdminHub } from '../lib/adminHubPermissions';
import { allowlistRoleLabel } from '../lib/settingsPermissions';
import {
  IS_UNIFIED_PORTAL,
  isChooserVerifyingHold,
  isSoftPortalEntryPending,
  portalPath,
  type PortalKind,
} from '../lib/portal';
import { useGoToPortal } from '../hooks/useGoToPortal';
import { Navigate, useNavigate } from 'react-router-dom';

type WorkspaceOption = {
  key: string;
  title: string;
  icon: string;
  className?: string;
  pathHint: string;
  roleLabel?: string | null;
  onOpen: () => void;
};

export function PortalChoosePage() {
  const { profile, portalAccess, signOut, refreshProfile, accessStatus } = useAuth();
  const navigate = useNavigate();
  const goToPortal = useGoToPortal();
  const name = profile?.display_name?.trim() || profile?.username || 'there';
  const showVerifying =
    isChooserVerifyingHold() ||
    (accessStatus === 'checking' && (Boolean(portalAccess) || isSoftPortalEntryPending()));

  if (!portalAccess) {
    return (
      <div className="login-page portal-choose-page">
        <div className="login-card portal-choose-card">
          <LoginBrand />
          <div className="login-card__body">
            <p className="login-card__lead portal-choose-lead">
              Welcome. Select a workspace to continue.
            </p>
          </div>
        </div>
        <VerifyingOverlay message="Checking access…" />
      </div>
    );
  }

  const crmAccess = portalAccess.crm.hasAccess;
  const itAccess = portalAccess.it.hasAccess;
  const demAccess = portalAccess.dem.hasAccess;
  const formsAccess = portalAccess.forms.hasAccess;
  const reportAccess = portalAccess.report.hasAccess;
  const accountsAccess = portalAccess.accounts.hasAccess;
  const purchasingAccess = portalAccess.purchasing.hasAccess;
  const designAccess = portalAccess.design.hasAccess;
  const showSettings = canAccessAdminHub(portalAccess);

  if (
    !crmAccess &&
    !itAccess &&
    !demAccess &&
    !formsAccess &&
    !accountsAccess &&
    !reportAccess &&
    !purchasingAccess &&
    !designAccess
  ) {
    return <Navigate to="/not-authorized" replace />;
  }

  function pathFor(kind: PortalKind, fallback: string): string {
    return IS_UNIFIED_PORTAL ? portalPath(kind) : fallback;
  }

  const workspaces: WorkspaceOption[] = [];

  if (crmAccess) {
    workspaces.push({
      key: 'crm',
      title: 'Customer Relationship Management',
      icon: '◎',
      pathHint: pathFor('crm', 'crm.banteckhan.com.sg'),
      roleLabel: portalAccess.crm.profile?.role
        ? `CRM role: ${allowlistRoleLabel(portalAccess.crm.profile.role)}`
        : null,
      onOpen: () => void goToPortal('crm'),
    });
  }

  if (itAccess) {
    workspaces.push({
      key: 'it',
      title: 'IT Management',
      icon: '⚙',
      className: 'portal-choose-option--it',
      pathHint: pathFor('it', 'it.banteckhan.com.sg'),
      roleLabel: portalAccess.it.profile?.role
        ? `IT role: ${allowlistRoleLabel(portalAccess.it.profile.role)}`
        : null,
      onOpen: () => void goToPortal('it'),
    });
  }

  if (demAccess) {
    workspaces.push({
      key: 'dem',
      title: 'Data Entry Management',
      icon: '📋',
      className: 'portal-choose-option--dem',
      pathHint: pathFor('dem', 'portal.banteckhan.com.sg/dem'),
      roleLabel: portalAccess.dem.profile?.role
        ? `DEM role: ${allowlistRoleLabel(portalAccess.dem.profile.role)}`
        : null,
      onOpen: () => void goToPortal('dem'),
    });
  }

  if (formsAccess) {
    workspaces.push({
      key: 'forms',
      title: 'Application Form',
      icon: '📝',
      pathHint: pathFor('forms', 'portal.banteckhan.com.sg/forms'),
      roleLabel: portalAccess.forms.profile?.role
        ? `Forms role: ${allowlistRoleLabel(portalAccess.forms.profile.role)}`
        : null,
      onOpen: () => void goToPortal('forms'),
    });
  }

  if (accountsAccess) {
    workspaces.push({
      key: 'accounts',
      title: 'Accounts',
      icon: '💰',
      pathHint: pathFor('accounts', 'portal.banteckhan.com.sg/accounts'),
      roleLabel: portalAccess.accounts.profile?.role
        ? `Role: ${allowlistRoleLabel(portalAccess.accounts.profile.role)}`
        : null,
      onOpen: () => void goToPortal('accounts'),
    });
  }

  if (reportAccess) {
    workspaces.push({
      key: 'report',
      title: 'Report',
      icon: '📊',
      className: 'portal-choose-option--report',
      pathHint: pathFor('report', 'portal.banteckhan.com.sg/report'),
      roleLabel: portalAccess.report.profile?.role
        ? `Role: ${allowlistRoleLabel(portalAccess.report.profile.role)}`
        : null,
      onOpen: () => void goToPortal('report'),
    });
  }

  if (purchasingAccess) {
    workspaces.push({
      key: 'purchasing',
      title: 'Purchasing',
      icon: '🛒',
      className: 'portal-choose-option--purchasing',
      pathHint: pathFor('purchasing', 'portal.banteckhan.com.sg/purchasing'),
      roleLabel: portalAccess.purchasing.profile?.role
        ? `Role: ${allowlistRoleLabel(portalAccess.purchasing.profile.role)}`
        : null,
      onOpen: () => void goToPortal('purchasing'),
    });
  }

  if (designAccess) {
    workspaces.push({
      key: 'design',
      title: 'Design',
      icon: '✦',
      className: 'portal-choose-option--design',
      pathHint: pathFor('design', 'portal.banteckhan.com.sg/design'),
      roleLabel: portalAccess.design.profile?.role
        ? `Role: ${allowlistRoleLabel(portalAccess.design.profile.role)}`
        : null,
      onOpen: () => void goToPortal('design'),
    });
  }

  if (showSettings) {
    workspaces.push({
      key: 'settings',
      title: 'Settings',
      icon: '⚙',
      className: 'portal-choose-option--settings',
      pathHint: '/admin',
      onOpen: () => {
        navigate('/admin');
        void refreshProfile();
      },
    });
  }

  return (
    <div className="login-page portal-choose-page">
      <div className="login-card portal-choose-card">
        <LoginBrand />
        <div className="login-card__body">
          <p className="login-card__lead portal-choose-lead">
            Welcome, {name}. Select a workspace to continue.
          </p>

          <div className="portal-choose-grid">
            {workspaces.map((ws) => (
              <button
                key={ws.key}
                type="button"
                className={['portal-choose-option', ws.className].filter(Boolean).join(' ')}
                onClick={ws.onOpen}
                disabled={showVerifying}
              >
                <span className="portal-choose-option__icon" aria-hidden>
                  {ws.icon}
                </span>
                <span className="portal-choose-option__title">{ws.title}</span>
                <span className="portal-choose-option__meta">
                  <span className="portal-choose-option__hint">{ws.pathHint}</span>
                  {ws.roleLabel ? (
                    <span className="portal-choose-option__role">{ws.roleLabel}</span>
                  ) : null}
                </span>
              </button>
            ))}
          </div>

          {workspaces.length > 1 ? (
            <p className="portal-choose-footnote">
              All workspaces use the same Microsoft sign-in.
            </p>
          ) : null}

          <button
            type="button"
            className="btn-secondary btn-sm portal-choose-signout"
            onClick={() => void signOut()}
            disabled={showVerifying}
          >
            Sign out
          </button>
        </div>
      </div>

      {showVerifying ? <VerifyingOverlay /> : null}
    </div>
  );
}
